import {
  scaleBand,
  scaleQuantize,
  scaleThreshold,
  scaleTime,
} from "@visx/scale";
import type { ScaleTime } from "d3-scale";
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInMonths,
  differenceInWeeks,
} from "date-fns";
import type {
  Accessor,
  ScaleFunctionsProps,
  TimeSeriesIntervalType,
} from "../types";
import { Data } from "../types";

export const compose = (scale: any, accessor: Accessor) => (data: any) =>
  scale(accessor(data));

const getDifferenceFunction = (
  from: Date,
  to: Date,
  interval: TimeSeriesIntervalType = "1day"
) => {
  const dayDifference = differenceInDays(from, to);

  switch (interval) {
    case "1minute":
      return differenceInMinutes;
    case "5minute":
      return (from: Date, to: Date) =>
        Math.floor(differenceInMinutes(from, to) / 5);
    case "15minute":
      return (from: Date, to: Date) =>
        Math.floor(differenceInMinutes(from, to) / 15);
    case "1hour":
      return differenceInHours;
    case "1day":
      if (dayDifference <= 1) {
        return differenceInHours;
      }

      return differenceInDays;
    case "7day":
    case "30day":
      return differenceInWeeks;
    case "365day":
      return differenceInMonths;
    default:
      return differenceInDays;
  }
};

export const getHexColourWithOpacity = (hex: string, opacity: number) =>
  `${hex}${opacity < 100 ? opacity : ""}`;

const setsToDomain = ({
  data,
  comparator,
  bottomAccessor,
  bottomFormatter,
}: {
  data: Data;
  comparator: (aValue: any, bValue: any) => 1 | 0 | -1;
  bottomAccessor: Accessor;
  bottomFormatter?: (value: any) => string;
}) =>
  data
    .reduce((total: any[], { data }) => [...total, ...data], [])
    // We are using a band scale for maximum flexibility, but this forces us to handle sorting ourselves
    .sort(comparator)
    // We don't want duplicate bands
    .reduce((total: number[], info) => {
      const value = bottomAccessor(info);

      const formattedValue = bottomFormatter
        ? bottomFormatter(value.toString())
        : value;

      return !total.includes(formattedValue)
        ? [...total, formattedValue]
        : total;
    }, []);

export const getTimeScaleHelpers = ({
  xMax,
  data,
  comparator,
  bottomAccessor,
  tickRenderer,
  from,
  to,
  interval,
}: ScaleFunctionsProps & {
  from: Date;
  to: Date;
  interval?: TimeSeriesIntervalType;
}) => {
  const domain = setsToDomain({
    data,
    comparator,
    bottomAccessor,
    bottomFormatter: tickRenderer,
  });

  const scale: TimeScale = scaleTime({
    range: [0, xMax],
    round: true,
    domain: [from, to],
  });

  const thresholdScale = scaleThreshold({
    range: [0, ...domain],
    domain: domain.map((date: Date) => new Date(date).getTime()),
  });

  const quantizedScale = compose(thresholdScale, scale.invert as Accessor);

  const getPoint = compose(
    scale,
    (dataPoint) => new Date(bottomAccessor(dataPoint))
  );

  const ticks = scale.ticks();

  const differenceFunction = getDifferenceFunction(
    ticks[0],
    ticks[1],
    interval
  );

  const bandWidth = (xMax / differenceFunction(to, from)) * 0.95;

  const getNearestPosition = (mouseX: number) => {
    return (
      (scale(new Date(thresholdScale(scale.invert(mouseX)) as string)) || 0.5) +
      bandWidth / 2
    );
  };

  scale.bandwidth = () => bandWidth;
  scale.round = () => undefined;

  return {
    scale,
    quantizedScale,
    getPoint,
    getNearestPosition,
  };
};

export const getBandScaleHelpers = ({
  xMax,
  data,
  comparator,
  bottomAccessor,
  variant = "line",
  tickRenderer,
}: ScaleFunctionsProps): any /* IMPROVE: TS2742 workaround */ => {
  let paddingOuter = -0.33;

  if (variant === "brush") {
    paddingOuter = 0.0;
  }

  if (variant === "bar") {
    paddingOuter = 0.0;
  }

  const scale = scaleBand({
    range: [0, xMax],
    round: true,
    domain: setsToDomain({
      data,
      comparator,
      bottomAccessor,
      bottomFormatter: tickRenderer,
    }),
    paddingInner: 0.4,
    paddingOuter,
  });

  const getPoint = compose(scale, (dataPoint) => {
    const value = bottomAccessor(dataPoint);
    return tickRenderer ? tickRenderer(value.toString()) : value;
  });

  const quantizedScale =
    scale?.domain()?.length && scale?.range()?.length === 2
      ? scaleQuantize({
          range: scale?.domain(),
          domain: scale?.range(),
        })
      : () => 0;

  const getNearestPosition = (mouseX: number) =>
    (scale(quantizedScale(mouseX)) ?? 0) + scale.bandwidth() / 2;

  return { scale, quantizedScale, getPoint, getNearestPosition };
};

export type TimeScale = {
  bandwidth?: () => number;
  round?: () => void;
  paddingInner?: () => number;
} & ScaleTime<number, number, never>;
